import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Car, Users, MapPin, CreditCard, Shield, Clock, Globe, ChevronDown, Volume2, VolumeX, Loader2, HelpCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { languages } from '@/i18n';
import HelpCenter from '@/components/HelpCenter';
import AppStoresSection from '@/components/AppStoresSection';
import { Capacitor } from '@capacitor/core';

const API = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const { t, i18n } = useTranslation();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const videoSectionRef = useRef(null);

  // Tracking QR / campagnes : si l'URL contient ?ref=xxx ou ?campaign=xxx, on enregistre le scan
  // et on persiste la valeur en localStorage pour la binder au signup ensuite.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = (params.get('ref') || params.get('campaign') || '').trim().toLowerCase();
    if (ref && ref !== 'qr') { // 'qr' générique = pas une campagne identifiée
      try {
        localStorage.setItem('signup_campaign', ref);
        // Dedupe : 1 scan max par session par campagne (évite double-scan en dev StrictMode + reloads)
        const sessKey = `qr_scan_sent_${ref}`;
        if (!sessionStorage.getItem(sessKey)) {
          sessionStorage.setItem(sessKey, '1');
          const apiUrl = process.env.REACT_APP_BACKEND_URL;
          if (apiUrl) {
            fetch(`${apiUrl}/api/qr/scan`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ campaign: ref, referrer: document.referrer || '' }),
              keepalive: true,
            }).catch(() => {});
          }
        }
      } catch (_e) { /* ignore */ }
    }
  }, []);

  // Get language code - keep full code if it exists in languages list, otherwise get base
  const getLanguageCode = (langCode) => {
    if (!langCode) return 'fr';
    const fullCode = langCode.split('@')[0];
    if (languages.find(l => l.code === fullCode)) {
      return fullCode;
    }
    return fullCode.split('-')[0];
  };
  
  const currentLanguage = languages.find(l => l.code === getLanguageCode(i18n.language)) || languages[0];

  const changeLanguage = (code) => {
    // Stop audio when changing language
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setAudioPlaying(false);
    }
    i18n.changeLanguage(code);
    setLanguageMenuOpen(false);
  };

  // Audio player - loads pre-generated static MP3 files from public/audio/voiceover
  const playVoiceover = () => {
    // If already playing, stop
    if (audioPlaying && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      setAudioPlaying(false);
      return;
    }

    // Show loading state
    setAudioLoading(true);

    // Determine language code (preserve en-GB if active)
    const rawLang = i18n.language || 'fr';
    const langCode = languages.find(l => l.code === rawLang) ? rawLang : rawLang.split('-')[0];
    const audioUrl = `/audio/voiceover/voiceover_${langCode}.mp3`;

    const audio = new Audio(audioUrl);
    audioRef.current = audio;

    audio.oncanplaythrough = () => {
      setAudioLoading(false);
      audio.play()
        .then(() => {
          setAudioPlaying(true);
        })
        .catch((err) => {
          console.warn('Play failed:', err);
          setAudioLoading(false);
          setAudioPlaying(false);
        });
    };

    audio.onended = () => {
      setAudioPlaying(false);
      audioRef.current = null;
    };

    audio.onerror = () => {
      console.error('Audio load error for', audioUrl);
      setAudioLoading(false);
      setAudioPlaying(false);
      audioRef.current = null;
    };

    audio.load();
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: 'url(https://images.unsplash.com/photo-1768297941301-1009a05e5514?crop=entropy&cs=srgb&fm=jpg&q=85)',
            filter: 'brightness(0.3)'
          }}
        />
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#09090B]/50 via-transparent to-[#09090B]" />
        
        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-50 px-6 py-4">
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Car className="w-8 h-8 text-[#FFD60A]" />
              <span className="text-2xl font-black text-white tracking-tight">MÉTRO-TAXI</span>
            </div>
            <div className="flex items-center gap-4">
              {/* Language Selector */}
              <div className="relative">
                <Button 
                  variant="ghost" 
                  className="text-white hover:text-[#FFD60A] flex items-center gap-2"
                  onClick={() => setLanguageMenuOpen(!languageMenuOpen)}
                  data-testid="language-selector-btn"
                >
                  <Globe className="w-5 h-5" />
                  <span className="hidden sm:inline">{currentLanguage.flag} {currentLanguage.name}</span>
                  <span className="sm:hidden">{currentLanguage.flag}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${languageMenuOpen ? 'rotate-180' : ''}`} />
                </Button>
                
                <AnimatePresence>
                  {languageMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="absolute right-0 mt-2 w-52 bg-[#18181B] border border-zinc-700 rounded-lg shadow-xl overflow-hidden z-50 max-h-80 overflow-y-auto"
                    >
                      {languages.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => changeLanguage(lang.code)}
                          className={`w-full px-4 py-2.5 text-left flex items-center gap-3 hover:bg-zinc-800 transition-colors text-sm ${
                            i18n.language === lang.code ? 'bg-[#FFD60A]/10 text-[#FFD60A]' : 'text-white'
                          }`}
                          data-testid={`lang-${lang.code}`}
                        >
                          <span className="text-lg">{lang.flag}</span>
                          <span>{lang.name}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Help Button */}
              <Link to="/support">
                <Button 
                  className="bg-[#FFD60A] text-black font-bold hover:bg-[#E6C209] hidden sm:flex items-center gap-2"
                  data-testid="help-nav-btn"
                >
                  <HelpCircle className="w-4 h-4" />
                  {t('help.button', 'AIDE')}
                </Button>
              </Link>

              {/* Install PWA Button — failsafe : reste accessible même si le banner a été dismiss */}
              {/* ⚡ Masqué en app iOS/Android native Capacitor (déjà installée) pour laisser la place au bouton Connexion */}
              {!Capacitor.isNativePlatform() && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) && !window.matchMedia('(display-mode: standalone)').matches && (
                <Button
                  className="bg-zinc-800 hover:bg-zinc-700 text-[#FFD60A] border border-[#FFD60A] font-bold flex items-center gap-2 text-xs px-3"
                  onClick={() => {
                    localStorage.removeItem('pwa-banner-dismissed');
                    const url = new URL(window.location.href);
                    url.searchParams.set('install', '1');
                    window.location.href = url.toString();
                  }}
                  data-testid="install-pwa-nav-btn"
                  title="Installer Métro-Taxi sur ton écran d'accueil"
                >
                  📲 Installer
                </Button>
              )}

              <Link to="/login">
                <Button variant="ghost" className="text-white hover:text-[#FFD60A]" data-testid="login-nav-btn">
                  {t('nav.login')}
                </Button>
              </Link>
            </div>
          </div>
        </nav>
        
        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Eco Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/50 px-4 py-2 rounded-full mb-6"
            >
              <span className="text-green-400 text-2xl">🌿</span>
              <span className="text-green-400 font-medium text-sm md:text-base">{t('hero.ecoSlogan')}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              {t('hero.title')}
              <br />
              <span className="text-[#FFD60A]">{t('hero.subtitleHighlight')}</span>
            </h1>
            <p className="text-xl md:text-2xl text-zinc-300 mb-4 max-w-2xl mx-auto">
              {t('hero.description')}
            </p>
            
            {/* Environmental message */}
            <p className="text-sm md:text-base text-green-400 mb-10 max-w-xl mx-auto flex items-center justify-center gap-2">
              <span>🌍</span>
              {t('hero.ecoMessage')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register/user">
                <Button 
                  size="lg" 
                  className="bg-[#FFD60A] text-black font-bold text-lg px-8 py-6 hover:bg-[#E6C209] btn-press"
                  data-testid="signup-user-btn"
                >
                  {t('pricing.cta')}
                </Button>
              </Link>
              <Link to="/register/driver">
                <Button 
                  size="lg" 
                  className="bg-white text-black font-bold text-lg px-8 py-6 hover:bg-zinc-200 btn-press"
                  data-testid="signup-driver-btn"
                >
                  🚖 {t('drivers.cta')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>
      
      {/* Video Section */}
      <section ref={videoSectionRef} className="py-20 px-6 bg-[#09090B]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {t('video.title').split(' ')[0]} <span className="text-[#FFD60A]">MÉTRO-TAXI</span>
            </h2>
            <p className="text-zinc-400 text-lg">{t('hero.subtitle')}</p>
          </motion.div>
          
          {/* Video Player - AI Generated Promo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="relative aspect-video bg-zinc-900 rounded-lg overflow-hidden border border-zinc-800"
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              controls
              autoPlay
              muted
              loop
              playsInline
              poster="https://images.unsplash.com/photo-1768297941301-1009a05e5514?crop=entropy&cs=srgb&fm=jpg&q=85"
            >
              <source src="/videos/metro-taxi-promo.mp4" type="video/mp4" />
              {t('common.error')}
            </video>
            
            {/* Voiceover Button - Simple synchronous click handler for mobile compatibility */}
            <button
              onClick={playVoiceover}
              disabled={audioLoading}
              className={`absolute bottom-4 right-4 flex items-center gap-2 px-4 py-2 rounded-full font-bold transition-all ${
                audioPlaying 
                  ? 'bg-[#FFD60A] text-black' 
                  : audioLoading
                    ? 'bg-zinc-700 text-zinc-400 cursor-wait'
                    : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              data-testid="voiceover-btn"
            >
              {audioLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="hidden sm:inline">{t('video.loading', 'Chargement...')}</span>
                </>
              ) : audioPlaying ? (
                <>
                  <VolumeX className="w-5 h-5" />
                  <span className="hidden sm:inline">{t('video.stop', 'Stop')}</span>
                </>
              ) : (
                <>
                  <Volume2 className="w-5 h-5" />
                  <span className="hidden sm:inline">{currentLanguage.flag} {t('video.listen', 'Écouter')}</span>
                </>
              )}
            </button>
          </motion.div>
          
          <p className="text-center text-zinc-500 text-sm mt-4">
            🎬 {t('video.title')} - {t('hero.subtitle')}
          </p>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-20 px-6 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {t('howItWorks.title')}
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: CreditCard,
                title: t('howItWorks.steps.step1.title'),
                description: t('howItWorks.steps.step1.description')
              },
              {
                icon: MapPin,
                title: t('howItWorks.steps.step2.title'),
                description: t('howItWorks.steps.step2.description')
              },
              {
                icon: Car,
                title: t('howItWorks.steps.step3.title'),
                description: t('howItWorks.steps.step3.description')
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className="bg-[#18181B] border border-zinc-800 p-8 text-center"
              >
                <div className="w-16 h-16 bg-[#FFD60A] rounded-sm flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-zinc-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Pricing Preview */}
      <section className="py-20 px-6 bg-[#09090B]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-zinc-400 text-lg">{t('pricing.subtitle')}</p>
          </motion.div>

          {/* Regional Pricing Tabs */}
          <div className="mb-8">
            <div className="flex justify-center gap-4 flex-wrap">
              <span className="px-4 py-2 bg-[#FFD60A] text-black font-bold rounded-full flex items-center gap-2">
                🇫🇷 {t('pricing.regions.paris', 'Île-de-France')}
              </span>
              <span className="px-4 py-2 bg-zinc-800 text-zinc-400 font-medium rounded-full flex items-center gap-2 opacity-70">
                🇪🇸 {t('pricing.regions.madrid', 'Madrid')} <span className="text-xs">({t('pricing.comingSoon', 'Bientôt')})</span>
              </span>
              <span className="px-4 py-2 bg-zinc-800 text-zinc-400 font-medium rounded-full flex items-center gap-2 opacity-70">
                🇬🇧 {t('pricing.regions.london', 'Londres')} <span className="text-xs">({t('pricing.comingSoon', 'Bientôt')})</span>
              </span>
            </div>
          </div>
          
          {/* France Pricing (Active) */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto mb-16">
            {[
              { name: t('pricing.plans.day.name'), price: t('pricing.plans.day.priceLocal'), period: t('subscription.perDay'), limit: t('pricing.limits.day', '5 trajets max sur 24h') },
              { name: t('pricing.plans.week.name'), price: t('pricing.plans.week.priceLocal'), period: t('subscription.perWeek'), limit: t('pricing.limits.week', '15 trajets sur 7j (3/jour)') },
              { name: t('pricing.plans.month.name'), price: t('pricing.plans.month.priceLocal'), period: t('subscription.perMonth'), popular: true, limit: t('pricing.limits.month', 'Trajets illimités') }
            ].map((plan, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`subscription-card ${plan.popular ? 'popular' : ''}`}
              >
                {plan.popular && (
                  <span className="popular-badge">{t('pricing.popularBadge')}</span>
                )}
                <h3 className="text-lg font-bold text-zinc-400 mb-4">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-black text-[#FFD60A]">{plan.price}</span>
                </div>
                <p className="text-zinc-500 mb-6">{plan.period}</p>
                <ul className="space-y-3 text-zinc-300 mb-8">
                  <li className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#FFD60A]" />
                    {plan.limit}
                  </li>
                  <li className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#FFD60A]" />
                    {t('pricing.features.allVehicles')}
                  </li>
                  <li className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#FFD60A]" />
                    {t('pricing.features.eco')}
                  </li>
                </ul>
                <Link to="/register/user">
                  <Button 
                    className={`w-full ${plan.popular ? 'bg-[#FFD60A] text-black hover:bg-[#E6C209]' : 'bg-zinc-800 text-white hover:bg-zinc-700'} font-bold`}
                    data-testid={`plan-${index}-btn`}
                  >
                    {t('pricing.cta')}
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Upcoming Markets Preview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-8 max-w-6xl mx-auto"
          >
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              {t('pricing.upcomingMarkets', 'Tarifs dans nos prochains marchés')}
            </h3>
            
            {/* MADRID - All Zones */}
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🇪🇸</span>
                <h4 className="text-xl font-bold text-white">{t('pricing.zones.madrid.title', 'Madrid')}</h4>
                <span className="text-zinc-500 text-sm">({t('pricing.comingSoon', 'Bientôt disponible')})</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Zone A */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-[#FFD60A] font-bold text-sm mb-3">{t('pricing.zones.madrid.zoneA.name', 'Zone A')}</p>
                  <p className="text-zinc-500 text-xs mb-2">{t('pricing.zones.madrid.zoneA.desc', 'Madrid Centro, Alcorcón, Getafe, Leganés')}</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.day', '24h')}</span><span className="font-bold text-[#FFD60A]">€4.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.week', '1 Sem.')}</span><span className="font-bold text-[#FFD60A]">€14.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.month', '1 Mois')}</span><span className="font-bold text-[#FFD60A]">€34.99</span>
                    </div>
                  </div>
                </div>
                {/* Zones B1-B2 */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-[#FFD60A] font-bold text-sm mb-3">{t('pricing.zones.madrid.zoneB.name', 'Zones B1-B2')}</p>
                  <p className="text-zinc-500 text-xs mb-2">{t('pricing.zones.madrid.zoneB.desc', 'Móstoles, Fuenlabrada, Parla, Arganda')}</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.day', '24h')}</span><span className="font-bold text-[#FFD60A]">€5.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.week', '1 Sem.')}</span><span className="font-bold text-[#FFD60A]">€17.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.month', '1 Mois')}</span><span className="font-bold text-[#FFD60A]">€44.99</span>
                    </div>
                  </div>
                </div>
                {/* Zones B3/C1/C2 */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-[#FFD60A] font-bold text-sm mb-3">{t('pricing.zones.madrid.zoneC.name', 'Zones B3/C1/C2')}</p>
                  <p className="text-zinc-500 text-xs mb-2">{t('pricing.zones.madrid.zoneC.desc', 'Alcalá de Henares, Torrejón de Ardoz')}</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.day', '24h')}</span><span className="font-bold text-[#FFD60A]">€6.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.week', '1 Sem.')}</span><span className="font-bold text-[#FFD60A]">€20.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.month', '1 Mois')}</span><span className="font-bold text-[#FFD60A]">€54.99</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* LONDON - All Zones */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-3xl">🇬🇧</span>
                <h4 className="text-xl font-bold text-white">{t('pricing.zones.london.title', 'London')}</h4>
                <span className="text-zinc-500 text-sm">({t('pricing.comingSoon', 'Bientôt disponible')})</span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Zones 1-2 */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-[#FFD60A] font-bold text-sm mb-3">{t('pricing.zones.london.zone12.name', 'Zones 1-2')}</p>
                  <p className="text-zinc-500 text-xs mb-2">{t('pricing.zones.london.zone12.desc', 'Central London, Camden, Greenwich')}</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.day', '24h')}</span><span className="font-bold text-[#FFD60A]">£9.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.week', '1 Week')}</span><span className="font-bold text-[#FFD60A]">£34.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.month', '1 Month')}</span><span className="font-bold text-[#FFD60A]">£79.99</span>
                    </div>
                  </div>
                </div>
                {/* Zones 1-4 */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-[#FFD60A] font-bold text-sm mb-3">{t('pricing.zones.london.zone14.name', 'Zones 1-4')}</p>
                  <p className="text-zinc-500 text-xs mb-2">{t('pricing.zones.london.zone14.desc', '+ Stratford, Wimbledon, Wembley')}</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.day', '24h')}</span><span className="font-bold text-[#FFD60A]">£14.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.week', '1 Week')}</span><span className="font-bold text-[#FFD60A]">£49.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.month', '1 Month')}</span><span className="font-bold text-[#FFD60A]">£129.99</span>
                    </div>
                  </div>
                </div>
                {/* Zones 1-6 */}
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <p className="text-[#FFD60A] font-bold text-sm mb-3">{t('pricing.zones.london.zone16.name', 'Zones 1-6')}</p>
                  <p className="text-zinc-500 text-xs mb-2">{t('pricing.zones.london.zone16.desc', 'Greater London + Heathrow')}</p>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.day', '24h')}</span><span className="font-bold text-[#FFD60A]">£19.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.week', '1 Week')}</span><span className="font-bold text-[#FFD60A]">£69.99</span>
                    </div>
                    <div className="flex justify-between text-zinc-300">
                      <span>{t('pricing.zones.duration.month', '1 Month')}</span><span className="font-bold text-[#FFD60A]">£149</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Driver Section - Complete */}
      <section className="py-20 px-6 bg-[#09090B]" id="chauffeurs">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
              {t('drivers.title')}
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              {t('drivers.subtitle')}
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Revenue Card - Multi-Region */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#FFD60A] to-[#E6C209] p-8 rounded-lg"
            >
              <h3 className="text-2xl font-black text-black mb-6">{t('drivers.revenue.title')}</h3>
              
              {/* Paris/France */}
              <div className="bg-black/10 backdrop-blur-sm rounded-lg p-4 mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-black font-bold flex items-center gap-2">
                    <span className="text-xl">🇫🇷</span> {t('drivers.revenue.byRegion.paris.label')}
                  </span>
                  <div className="text-right">
                    <span className="text-2xl font-black text-black">{t('drivers.revenue.byRegion.paris.rangeMin')} - {t('drivers.revenue.byRegion.paris.rangeMax')}</span>
                    <p className="text-xs text-black/60">{t('drivers.revenue.monthly')} {t('drivers.revenue.launchNote')}</p>
                  </div>
                </div>
              </div>

              {/* Madrid/Spain */}
              <div className="bg-black/10 backdrop-blur-sm rounded-lg p-4 mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-black font-bold flex items-center gap-2">
                    <span className="text-xl">🇪🇸</span> {t('drivers.revenue.byRegion.madrid.label')}
                  </span>
                  <div className="text-right">
                    <span className="text-2xl font-black text-black">{t('drivers.revenue.byRegion.madrid.rangeMin')} - {t('drivers.revenue.byRegion.madrid.rangeMax')}</span>
                    <p className="text-xs text-black/60">{t('drivers.revenue.monthly')} {t('drivers.revenue.launchNote')}</p>
                  </div>
                </div>
              </div>

              {/* London/UK */}
              <div className="bg-black/10 backdrop-blur-sm rounded-lg p-4 mb-4">
                <div className="flex items-center justify-between">
                  <span className="text-black font-bold flex items-center gap-2">
                    <span className="text-xl">🇬🇧</span> {t('drivers.revenue.byRegion.london.label')}
                  </span>
                  <div className="text-right">
                    <span className="text-2xl font-black text-black">{t('drivers.revenue.byRegion.london.rangeMin')} - {t('drivers.revenue.byRegion.london.rangeMax')}</span>
                    <p className="text-xs text-black/60">{t('drivers.revenue.monthly')} {t('drivers.revenue.launchNote')}</p>
                  </div>
                </div>
              </div>

              {/* Depending + Gross Revenue */}
              <div className="bg-black/10 backdrop-blur-sm rounded-lg p-3 mb-3 text-center">
                <p className="text-xs text-black/70">{t('drivers.revenue.depending')}</p>
                <p className="text-xs text-black/60 mt-1 font-semibold">{t('drivers.revenue.grossRevenue')}</p>
              </div>

              {/* Legal mentions */}
              <div className="bg-black/5 rounded-lg p-4 space-y-1">
                <p className="text-xs text-black/60">{t('drivers.legal.independence')}</p>
                <p className="text-xs text-black/60">{t('drivers.legal.platform')}</p>
                <p className="text-xs text-black/60">{t('drivers.legal.noSubordination')}</p>
                <p className="text-xs text-black/60">{t('drivers.legal.directRelation')}</p>
              </div>
            </motion.div>

            {/* Benefits List */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-white mb-8">{t('drivers.benefits.title')}</h3>
              <div className="space-y-4">
                {[
                  { icon: "💰", text: t('drivers.benefits.noSubscription'), desc: t('drivers.benefits.noSubscriptionDesc') },
                  { icon: "📊", text: t('drivers.benefits.noCommission'), desc: t('drivers.benefits.noCommissionDesc') },
                  { icon: "👥", text: t('drivers.benefits.subscribedPassengers'), desc: t('drivers.benefits.subscribedPassengersDesc') },
                  { icon: "🗺️", text: t('drivers.benefits.optimizedRoutes'), desc: t('drivers.benefits.optimizedRoutesDesc') },
                  { icon: "🚀", text: t('drivers.benefits.morePassengers'), desc: t('drivers.benefits.morePassengersDesc') }
                ].map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4 bg-zinc-900 border border-zinc-800 p-4 rounded-lg hover:border-[#FFD60A]/50 transition-colors"
                  >
                    <span className="text-2xl">{benefit.icon}</span>
                    <div>
                      <p className="font-bold text-white">{benefit.text}</p>
                      <p className="text-sm text-zinc-400">{benefit.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Driver App Preview */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-16 bg-zinc-900 border border-zinc-800 rounded-lg p-8"
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">{t('drivers.app.title')}</h3>
              <p className="text-zinc-400">{t('drivers.app.subtitle')}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: "📱", title: t('drivers.app.requests'), desc: t('drivers.app.requestsDesc') },
                { icon: "🛣️", title: t('drivers.app.routes'), desc: t('drivers.app.routesDesc') },
                { icon: "🔄", title: t('drivers.app.transfers'), desc: t('drivers.app.transfersDesc') }
              ].map((feature, index) => (
                <div key={index} className="text-center p-4">
                  <span className="text-4xl mb-4 block">{feature.icon}</span>
                  <h4 className="font-bold text-white mb-2">{feature.title}</h4>
                  <p className="text-sm text-zinc-400">{feature.desc}</p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <Link to="/register/driver">
              <Button 
                size="lg"
                className="bg-[#FFD60A] text-black font-bold text-lg px-10 py-6 hover:bg-[#E6C209] btn-press"
                data-testid="driver-cta-btn"
              >
                {t('drivers.cta')}
              </Button>
            </Link>
            <p className="text-zinc-500 mt-4 text-sm">{t('drivers.ctaSubtext')}</p>
          </motion.div>

          {/* Recrutement Chauffeurs — Contact direct */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-br from-[#FFD60A] to-[#E6C209] rounded-2xl p-8 md:p-10 text-center"
            data-testid="driver-contact-section"
          >
            <h3 className="text-3xl md:text-4xl font-black text-black mb-3">
              {t('drivers.contact.title', 'Une question ? Contactez-nous !')}
            </h3>
            <p className="text-black/80 text-lg mb-2 max-w-2xl mx-auto">
              {t('drivers.contact.subtitle', 'Notre équipe répond à toutes les questions des chauffeurs VTC intéressés par Métro-Taxi.')}
            </p>
            <p className="text-black font-bold text-base mb-8 max-w-2xl mx-auto">
              {t('drivers.contact.recommendation', "👉 Le plus rapide : inscrivez-vous directement en 3 minutes")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link 
                to="/register/driver"
                className="inline-flex items-center gap-3 bg-black text-white font-bold text-base sm:text-lg px-8 py-4 rounded-full hover:bg-zinc-800 transition-colors btn-press shadow-lg"
                data-testid="driver-register-inline-btn"
              >
                <span className="text-2xl">📝</span>
                <span>{t('drivers.contact.register', "S'inscrire en 3 min")}</span>
              </Link>
              <a
                href="mailto:contact@metro-taxi.com?subject=Question%20-%20Chauffeur%20VTC%20M%C3%A9tro-Taxi&body=Bonjour%2C%0A%0AJe%20suis%20chauffeur%20VTC%20et%20j%27ai%20une%20question%20avant%20de%20m%27inscrire%20sur%20Métro-Taxi%20%3A%0A%0A%5BPosez%20votre%20question%20ici%5D%0A%0A%F0%9F%91%89%20Pour%20vous%20inscrire%20directement%20%28recommand%C3%A9%29%20%3A%20https%3A%2F%2Fmetro-taxi.com%2Fregister%2Fdriver%0A%0ACordialement"
                className="inline-flex items-center gap-3 bg-white/90 text-black font-semibold text-base sm:text-lg px-8 py-4 rounded-full hover:bg-white transition-colors btn-press"
                data-testid="driver-email-btn"
              >
                <span className="text-2xl">📧</span>
                <span>{t('drivers.contact.question', 'Poser une question')}</span>
              </a>
            </div>
            <p className="text-black/60 text-sm mt-6">
              {t('drivers.contact.response', 'Réponse garantie sous 24h')} · {t('drivers.contact.free', 'Inscription 100% gratuite')}
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Patron VTC - B2B fleet partnerships */}
      <section className="py-12 px-6 border-t border-zinc-800 bg-gradient-to-b from-[#0A0A0B] to-[#09090B]" data-testid="landing-patron-vtc-cta">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#FFD60A]/10 via-[#18181B] to-[#18181B] border border-[#FFD60A]/30 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex-1 min-w-0">
              <p className="text-[#FFD60A] text-xs uppercase tracking-widest font-bold mb-2">Programme B2B</p>
              <h3 className="text-xl sm:text-2xl font-black text-white leading-tight mb-2">
                Tu diriges une <span className="text-[#FFD60A]">flotte VTC</span> ?
              </h3>
              <p className="text-zinc-400 text-sm">
                Partenariat <strong className="text-white">0 % commission</strong> · 1,50 € / km à tes chauffeurs · Paiement chaque lundi. Discutons.
              </p>
            </div>
            <Link to="/patron-vtc">
              <Button
                className="bg-[#FFD60A] text-black hover:bg-[#FFD60A]/90 px-6 h-12 font-bold whitespace-nowrap"
                data-testid="landing-patron-vtc-btn"
              >
                Partenariat Patron VTC →
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Commerces Partenaires */}
      <section className="py-12 px-6 bg-[#09090B]" data-testid="landing-commerces-cta">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-red-600/10 via-[#18181B] to-[#18181B] border border-red-600/30 rounded-2xl p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6"
          >
            <div className="flex-1 min-w-0">
              <p className="text-red-500 text-xs uppercase tracking-widest font-bold mb-2">Commerces · Boulangeries · Salons · Restaurants</p>
              <h3 className="text-xl sm:text-2xl font-black text-white leading-tight mb-2">
                Tu tiens un <span className="text-red-500">commerce de proximité</span> ?
              </h3>
              <p className="text-zinc-400 text-sm">
                Affiche notre QR code dans ta vitrine et reçois une commission à chaque <strong className="text-white">abonné</strong> que tu nous amènes.
                Inscription gratuite, paiement automatique chaque lundi.
              </p>
            </div>
            <Link to="/register/partner">
              <Button
                className="bg-red-600 text-white hover:bg-red-700 px-6 h-12 font-bold whitespace-nowrap"
                data-testid="landing-commerce-partner-btn"
              >
                Devenir partenaire commerce →
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* App Stores Section (soon on Google Play / App Store) */}
      <AppStoresSection />

      {/* Footer */}
      <footer className="py-12 px-6 bg-[#09090B] border-t border-zinc-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-2 mb-4 md:mb-0">
              <Car className="w-6 h-6 text-[#FFD60A]" />
              <span className="text-xl font-black text-white">MÉTRO-TAXI</span>
            </div>
            <div className="flex flex-wrap gap-6 md:gap-8 text-zinc-400 justify-center">
              <Link to="/login" className="hover:text-white transition-colors">{t('nav.login')}</Link>
              <Link to="/register/user" className="hover:text-white transition-colors">{t('nav.register')}</Link>
              <Link to="/register/driver" className="hover:text-white transition-colors">{t('nav.drivers')}</Link>
              <Link to="/patron-vtc" className="hover:text-[#FFD60A] transition-colors" data-testid="footer-patron-vtc-link">Patron VTC (B2B)</Link>
              <Link to="/register/partner" className="hover:text-red-400 transition-colors" data-testid="footer-commerces-link">Commerces partenaires</Link>
              <Link to="/cgu" className="hover:text-white transition-colors">{t('footer.cgu', 'CGU')}</Link>
              <Link to="/cgv" className="hover:text-white transition-colors">{t('footer.cgv', 'CGV')}</Link>
              <a 
                href="mailto:contact@metro-taxi.com" 
                className="hover:text-[#FFD60A] transition-colors font-semibold"
                data-testid="footer-contact-email"
              >
                📧 contact@metro-taxi.com
              </a>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-zinc-800 text-center text-zinc-500">
            <p>© {new Date().getFullYear()} Métro-Taxi. {t('footer.rights')}.</p>
            <p className="mt-2 text-xs text-zinc-600" data-testid="footer-siret">
              SIRET : 918 687 864 00010 — R.C.S. Bobigny
            </p>
          </div>
        </div>
      </footer>

      {/* Floating Help Button for Mobile */}
      <Link to="/support">
        <button
          className="fixed bottom-6 right-6 sm:hidden w-14 h-14 bg-[#FFD60A] text-black rounded-full shadow-lg flex items-center justify-center z-40 hover:bg-[#E6C209] transition-colors"
          data-testid="help-floating-btn"
        >
          <HelpCircle className="w-6 h-6" />
        </button>
      </Link>
    </div>
  );
};

export default Landing;
